import { isPageFocused } from 'features/script/use-clicker'
import { useEffect, useRef } from 'react'
import { presenterKeyCommand } from './model/presenter-keys'
import { PresenterCommand } from './model/presenter-messages'
import { isOwnFullscreen } from './window-management'

type Options = {
	goFullscreen: () => void
	/** Sent to the console, which acts on it. */
	command: (command: PresenterCommand) => void
}

/**
 * Keys pressed in the presentation window - usually the clicker, since this
 * window has the focus while full screen. The first press of a window not yet
 * full screen is its own click to go full screen, and nothing else; after
 * that, the clicker's Next and Prev and black screen go to the console.
 */
export const useProjectorKeys = ({ goFullscreen, command }: Options): void => {
	const latest = useRef({ goFullscreen, command })
	latest.current = { goFullscreen, command }

	useEffect(() => {
		const onKeyDown = (event: KeyboardEvent): void => {
			if (event.key === 'Escape' || event.repeat) {
				return
			}
			if (!isOwnFullscreen()) {
				event.preventDefault()
				event.stopImmediatePropagation()
				latest.current.goFullscreen()
				return
			}
			const pressed = presenterKeyCommand(event, {
				pageFocused: isPageFocused(),
			})
			if (pressed) {
				event.preventDefault()
				latest.current.command(pressed)
			}
		}
		window.addEventListener('keydown', onKeyDown, true)
		return () => window.removeEventListener('keydown', onKeyDown, true)
	}, [])
}
